import { useEffect, useMemo, useState } from 'react'
import { getTelemetryTrace, listTelemetryEvents, listTelemetryRequests, type TelemetryEventRow, type TelemetryRequestRow } from '../../api/telemetryAdmin'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import NotAuthorized from '../../components/auth/NotAuthorized'

type TabKey = 'requests' | 'events' | 'trace'
type QuickRange = '15m' | '1h' | '24h'

function toIsoFromNow(range: QuickRange): string {
  const now = Date.now()
  const deltaMs = range === '15m' ? 15 * 60 * 1000 : range === '1h' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000
  return new Date(now - deltaMs).toISOString()
}

function short(value?: string | null, len = 18): string {
  const text = String(value || '')
  if (!text) return '—'
  if (text.length <= len) return text
  return `${text.slice(0, len)}…`
}

export default function TelemetryLogsSettings() {
  const authed = Boolean(getToken())
  const { me, loading: meLoading } = useMe(authed)
  const [tab, setTab] = useState<TabKey>('requests')

  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [since, setSince] = useState(toIsoFromNow('1h'))

  const [requestRows, setRequestRows] = useState<TelemetryRequestRow[]>([])
  const [requestCursor, setRequestCursor] = useState<number | null>(null)
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<TelemetryRequestRow | null>(null)

  const [eventsRows, setEventsRows] = useState<TelemetryEventRow[]>([])
  const [eventsCursor, setEventsCursor] = useState<number | null>(null)
  const [eventName, setEventName] = useState('')
  const [eventsLoading, setEventsLoading] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<TelemetryEventRow | null>(null)

  const [traceRequestId, setTraceRequestId] = useState('')
  const [traceClientRequestId, setTraceClientRequestId] = useState('')
  const [traceFlowId, setTraceFlowId] = useState('')
  const [traceRequests, setTraceRequests] = useState<TelemetryRequestRow[]>([])
  const [traceEvents, setTraceEvents] = useState<TelemetryEventRow[]>([])
  const [traceLoading, setTraceLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canView = Boolean(me?.is_superuser)

  async function loadRequests(reset = true) {
    setRequestsLoading(true)
    setError(null)
    try {
      const data = await listTelemetryRequests({
        q: query || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        since: since || undefined,
        limit: 50,
        cursor: reset ? undefined : requestCursor || undefined,
      })
      setRequestRows((prev) => (reset ? data.results : [...prev, ...data.results]))
      setRequestCursor(data.next_cursor)
      if (reset) setSelectedRequest(data.results[0] || null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load request logs')
    } finally {
      setRequestsLoading(false)
    }
  }

  async function loadEvents(reset = true) {
    setEventsLoading(true)
    setError(null)
    try {
      const data = await listTelemetryEvents({
        name: eventName || undefined,
        since: since || undefined,
        limit: 50,
        cursor: reset ? undefined : eventsCursor || undefined,
      })
      setEventsRows((prev) => (reset ? data.results : [...prev, ...data.results]))
      setEventsCursor(data.next_cursor)
      if (reset) setSelectedEvent(data.results[0] || null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load event logs')
    } finally {
      setEventsLoading(false)
    }
  }

  async function runTrace() {
    setTraceLoading(true)
    setError(null)
    try {
      const data = await getTelemetryTrace({
        request_id: traceRequestId || undefined,
        client_request_id: traceClientRequestId || undefined,
        client_flow_id: traceFlowId || undefined,
      })
      setTraceRequests(data.requests || [])
      setTraceEvents(data.events || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to run trace')
      setTraceRequests([])
      setTraceEvents([])
    } finally {
      setTraceLoading(false)
    }
  }

  useEffect(() => {
    if (!canView) return
    void loadRequests(true)
    void loadEvents(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView])

  const mergedTrace = useMemo(() => {
    const requestItems = traceRequests.map((row) => ({
      at: row.created_at,
      kind: 'request' as const,
      label: `${row.method} ${row.path}`,
      detail: `${row.status} in ${row.duration_ms}ms`,
    }))
    const eventItems = traceEvents.map((row) => ({
      at: row.created_at,
      kind: 'event' as const,
      label: row.event_type,
      detail: row.severity,
    }))
    return [...requestItems, ...eventItems].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
  }, [traceEvents, traceRequests])

  if (!authed) return <NotAuthorized />
  if (meLoading) return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-white/70">Loading...</div>
  if (!canView) return <NotAuthorized />

  return (
    <div className="space-y-4" data-testid="telemetry-logs-page">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-white">Telemetry Logs</h1>
            <p className="text-sm text-white/65">Request logs, domain events, and trace lookup (admin only).</p>
          </div>
          <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
            Admin Only
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <div className="flex flex-wrap gap-2">
          {(['requests', 'events', 'trace'] as TabKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-lg border px-3 py-1.5 text-sm ${
                tab === key ? 'border-white/30 bg-white/15 text-white' : 'border-white/10 text-white/70 hover:bg-white/10'
              }`}
            >
              {key === 'requests' ? 'Requests' : key === 'events' ? 'Events' : 'Trace'}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>
      ) : null}

      {tab === 'requests' && (
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search path / ids / handle"
                className="min-w-[220px] flex-1 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-white/15 bg-black/30 px-2 py-2 text-sm text-white"
              >
                <option value="all">All</option>
                <option value="4xx">4xx</option>
                <option value="5xx">5xx</option>
                <option value="200">200</option>
              </select>
              <button type="button" onClick={() => setSince(toIsoFromNow('15m'))} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80">15m</button>
              <button type="button" onClick={() => setSince(toIsoFromNow('1h'))} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80">1h</button>
              <button type="button" onClick={() => setSince(toIsoFromNow('24h'))} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80">24h</button>
              <button type="button" onClick={() => void loadRequests(true)} className="rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-sm text-blue-200">
                Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-white/60">
                  <tr>
                    <th className="px-2 py-2">Time</th>
                    <th className="px-2 py-2">Method / Path</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Duration</th>
                    <th className="px-2 py-2">Handle</th>
                    <th className="px-2 py-2">Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {requestRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedRequest(row)}
                      className={`cursor-pointer border-t border-white/5 ${selectedRequest?.id === row.id ? 'bg-white/10' : 'hover:bg-white/5'}`}
                    >
                      <td className="px-2 py-2 text-white/75">{new Date(row.created_at).toLocaleString()}</td>
                      <td className="px-2 py-2 text-white">{row.method} {short(row.path, 48)}</td>
                      <td className="px-2 py-2">{row.status}</td>
                      <td className="px-2 py-2">{row.duration_ms}ms</td>
                      <td className="px-2 py-2">{row.actor_handle || row.user_id || '—'}</td>
                      <td className="px-2 py-2 font-mono text-xs text-white/60">{short(row.client_flow_id)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!requestCursor || requestsLoading}
                onClick={() => void loadRequests(false)}
                className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {requestsLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 lg:col-span-4">
            <h2 className="mb-2 text-sm font-semibold text-white/80">Request Detail</h2>
            <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-black/30 p-3 text-xs text-white/80">
              {selectedRequest ? JSON.stringify(selectedRequest, null, 2) : 'Select a row'}
            </pre>
          </div>
        </div>
      )}

      {tab === 'events' && (
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Event name (wallet.send*)"
                className="min-w-[220px] flex-1 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white"
              />
              <button type="button" onClick={() => setSince(toIsoFromNow('1h'))} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80">1h</button>
              <button type="button" onClick={() => setSince(toIsoFromNow('24h'))} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80">24h</button>
              <button type="button" onClick={() => void loadEvents(true)} className="rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-sm text-blue-200">
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-white/60">
                  <tr>
                    <th className="px-2 py-2">Time</th>
                    <th className="px-2 py-2">Event</th>
                    <th className="px-2 py-2">Severity</th>
                    <th className="px-2 py-2">Handle</th>
                    <th className="px-2 py-2">Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {eventsRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedEvent(row)}
                      className={`cursor-pointer border-t border-white/5 ${selectedEvent?.id === row.id ? 'bg-white/10' : 'hover:bg-white/5'}`}
                    >
                      <td className="px-2 py-2 text-white/75">{new Date(row.created_at).toLocaleString()}</td>
                      <td className="px-2 py-2 text-white">{row.event_type}</td>
                      <td className="px-2 py-2">{row.severity}</td>
                      <td className="px-2 py-2">{row.actor_handle || row.user_id || '—'}</td>
                      <td className="px-2 py-2 font-mono text-xs text-white/60">{short(row.client_flow_id)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!eventsCursor || eventsLoading}
                onClick={() => void loadEvents(false)}
                className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {eventsLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 lg:col-span-4">
            <h2 className="mb-2 text-sm font-semibold text-white/80">Event Detail</h2>
            <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-black/30 p-3 text-xs text-white/80">
              {selectedEvent ? JSON.stringify(selectedEvent, null, 2) : 'Select a row'}
            </pre>
          </div>
        </div>
      )}

      {tab === 'trace' && (
        <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="grid gap-2 md:grid-cols-3">
            <input
              value={traceRequestId}
              onChange={(e) => setTraceRequestId(e.target.value)}
              placeholder="X-Request-Id"
              className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white"
            />
            <input
              value={traceClientRequestId}
              onChange={(e) => setTraceClientRequestId(e.target.value)}
              placeholder="X-Client-Request-Id"
              className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white"
            />
            <input
              value={traceFlowId}
              onChange={(e) => setTraceFlowId(e.target.value)}
              placeholder="X-Client-Flow-Id"
              className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={() => void runTrace()}
              disabled={traceLoading}
              className="rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-sm text-blue-200 disabled:opacity-50"
            >
              {traceLoading ? 'Tracing…' : 'Trace'}
            </button>
          </div>
          <div className="space-y-2">
            {mergedTrace.length === 0 ? (
              <div className="text-sm text-white/65">No timeline entries yet.</div>
            ) : (
              mergedTrace.map((item, idx) => (
                <div key={`${item.kind}-${idx}`} className="rounded-lg border border-white/10 bg-black/20 p-3">
                  <div className="text-xs text-white/60">{new Date(item.at).toLocaleString()}</div>
                  <div className="text-sm text-white">
                    <span className="mr-2 rounded bg-white/10 px-1.5 py-0.5 text-xs uppercase">{item.kind}</span>
                    {item.label}
                  </div>
                  <div className="text-xs text-white/65">{item.detail}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

